/**
 * A medida das colunas do kanban, num lugar só.
 *
 * ## Por que isto existe
 *
 * O kanban é desenhado por DOIS componentes: `TicketPage` desenha a faixa de
 * cabeçalhos (que fica grudada no topo ao rolar) e `TicketKanbanView` desenha
 * os cartões, um grid por responsável. São grids irmãos que precisam ficar
 * alinhados coluna a coluna -- e, sendo dois lugares, eles divergiram.
 *
 * Medido na tela em 14/10/2026, com 5 colunas num container de 900px:
 *
 *   cabeçalho:  167px por coluna, sem transbordar  (espremia)
 *   corpo:      260px por coluna, transbordando    (rolava)
 *
 * Ou seja: o rótulo dizia que a coluna tinha 167px enquanto o cartão era
 * desenhado com 260px, e rolar os cartões não movia o cabeçalho. O piso de
 * 260px tinha sido posto só no corpo -- o comentário lá explica por que
 * `minmax(0, 1fr)` nunca transborda --, e o cabeçalho ficou para trás.
 *
 * É o mesmo modo de falha de sempre nesta tela: duas fontes para a mesma
 * resposta. Com a medida aqui, mexer nela move os dois.
 */

/**
 * O piso de largura de uma coluna.
 *
 * `minmax(260px, 1fr)` e não `minmax(0, 1fr)`: com mínimo ZERO o grid sempre
 * cabe na tela, então o scroll horizontal nunca entra em ação e acrescentar
 * coluna só espreme as outras até o cartão ficar ilegível. Com o piso, o grid
 * transborda -- e aí sim rola.
 *
 * Com poucas colunas nada muda: `1fr` já lhes dá mais que 260px.
 */
export const LARGURA_MINIMA_DA_COLUNA = 260;

/** O respiro entre colunas, em pixels. É o `gap: 1` do MUI. */
export const ESPACO_ENTRE_COLUNAS = 8;

const quantasColunas = (quantidade) => {
    const n = Number(quantidade);

    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
};

/**
 * O `grid-template-columns` do kanban.
 *
 * @param {number} quantidade quantas colunas o filtro definiu
 */
export const gradeDeColunas = (quantidade) =>
    `repeat(${quantasColunas(quantidade)}, minmax(${LARGURA_MINIMA_DA_COLUNA}px, 1fr))`;

/**
 * A largura que a grade ocupa quando todas as colunas estão no piso.
 *
 * É ela que faz o conjunto transbordar o container e produzir UM scroll
 * horizontal só, compartilhado pelo cabeçalho e por todos os grupos. Sem ela,
 * cada grid rolava por conta própria: rolar o grupo de um responsável não
 * movia nem o cabeçalho nem os outros grupos.
 *
 * @param {number} quantidade quantas colunas o filtro definiu
 * @returns {number} largura em pixels
 */
export const larguraMinimaDaGrade = (quantidade) => {
    const n = quantasColunas(quantidade);

    return n * LARGURA_MINIMA_DA_COLUNA + (n - 1) * ESPACO_ENTRE_COLUNAS;
};
